import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { useI18n } from "../lib/i18n";

/**
 * iter132 — Get Listed bottom sheet.
 *
 * Replaces the legacy `/get-listed` page. Opens from the ProfileMenu
 * "Get Listed" row. Two vertically-stacked option cards (no icons / no
 * checkboxes — the entire card border highlights when selected). The
 * fixed footer button reflects selection state:
 *   none    → gold outline, label "Select an option to continue", disabled
 *   biz     → gold gradient, label "Apply as Business",  → /get-listed/business
 *   vendor  → gold gradient, label "Apply as Vendor",    → /get-listed/vendor
 */
export default function GetListedSheet({ open, onClose }) {
  const { t } = useI18n();
  const nav = useNavigate();
  const [pending, setPending] = useState(null); // "business" | "vendor" | null

  if (!open) return null;

  const go = () => {
    if (!pending) return;
    onClose?.();
    nav(pending === "business" ? "/get-listed/business" : "/get-listed/vendor");
  };

  const ctaLabel = pending === "business"
    ? t("get_listed_business_cta")
    : pending === "vendor"
      ? t("get_listed_vendor_cta")
      : t("get_listed_select_hint");

  const ctaDisabled = !pending;

  return (
    <div
      data-testid="get-listed-overlay"
      className="fixed inset-0 z-[80] flex items-end justify-center"
      style={{ background: "rgba(0,0,0,0.6)" }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        data-testid="get-listed-sheet"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md flex flex-col relative"
        style={{
          background: "#171717",
          borderTop: "1px solid rgba(232,184,75,0.3)",
          borderTopLeftRadius: 14,
          borderTopRightRadius: 14,
          maxHeight: "92vh",
        }}
      >
        <div className="mx-auto mt-3" style={{ width: 32, height: 3, background: "#2a2a2a", borderRadius: 2 }} />

        <button
          onClick={onClose}
          aria-label="Close"
          data-testid="get-listed-close"
          className="absolute right-3 top-3 grid place-items-center transition-colors hover:bg-white/[0.04] rounded-full"
          style={{ width: 36, height: 36, color: "#E8B84B" }}
        >
          <X className="w-5 h-5" strokeWidth={1.5} />
        </button>

        <header className="px-5 mt-3 text-center">
          <h2
            data-testid="get-listed-title"
            style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: "#F5F1E8" }}
          >
            {t("menu_get_listed")}
          </h2>
          <p
            className="mt-1"
            style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#9A9490" }}
          >
            {t("get_listed_sheet_sub")}
          </p>
        </header>

        <div className="px-5 mt-5 pb-4 space-y-3 overflow-y-auto" style={{ flex: "1 1 auto" }}>
          <OptionCard
            kind="business"
            selected={pending === "business"}
            title={t("get_listed_business_title")}
            description={t("get_listed_business_sheet_desc")}
            tags={t("get_listed_business_tags")}
            onSelect={() => setPending("business")}
          />
          <OptionCard
            kind="vendor"
            selected={pending === "vendor"}
            title={t("get_listed_vendor_title")}
            description={t("get_listed_vendor_sheet_desc")}
            tags={t("get_listed_vendor_tags")}
            onSelect={() => setPending("vendor")}
          />
        </div>

        <div
          className="px-5 pb-5 pt-3"
          style={{ background: "#171717", borderTop: "1px solid rgba(255,255,255,0.04)" }}
        >
          <button
            type="button"
            onClick={go}
            disabled={ctaDisabled}
            data-testid="get-listed-cta"
            className="w-full transition-opacity"
            style={{
              height: 52,
              borderRadius: 12,
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 16,
              fontWeight: 700,
              letterSpacing: "0.02em",
              background: ctaDisabled
                ? "transparent"
                : "linear-gradient(135deg, #E8B84B 0%, #B58E3D 100%)",
              color: ctaDisabled ? "#E8B84B" : "#1a0f06",
              border: ctaDisabled ? "1px solid rgba(232,184,75,0.45)" : "none",
              opacity: ctaDisabled ? 0.85 : 1,
              cursor: ctaDisabled ? "not-allowed" : "pointer",
            }}
          >
            {ctaLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function OptionCard({ kind, selected, title, description, tags, onSelect }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      data-testid={`get-listed-card-${kind}`}
      aria-pressed={selected}
      className="text-left w-full transition-colors"
      style={{
        padding: "18px 18px 16px",
        background: selected ? "rgba(232,184,75,0.05)" : "#141414",
        border: selected ? "2px solid #E8B84B" : "2px solid #1f1f1f",
        borderRadius: 8,
      }}
    >
      <h3
        style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: 20,
          color: selected ? "#E8B84B" : "#F5F1E8",
          lineHeight: 1.2,
        }}
      >
        {title}
      </h3>
      <p
        className="mt-2"
        style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#9A9490", lineHeight: 1.5 }}
      >
        {description}
      </p>
      <p
        className="mt-2"
        style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#9A9490", lineHeight: 1.5 }}
      >
        {tags}
      </p>
    </button>
  );
}
